/**
 * SMS Test Tab
 * Draft and send test SMS messages
 */

import { useState, useEffect } from 'react';
import { useForm } from '@mantine/form';
import {
  IconSend,
  IconClock,
  IconAlertCircle,
  IconInfoCircle,
  IconPhone,
} from '@tabler/icons-react';
import { Stack, Group, Button, Alert, Select, TextInput, Switch, Textarea, Badge, Text, Divider } from '../../../../../components/ui';
import { Card } from '../../../../../components/ui/data-display';
import type { SmsTestTabProps, SmsTestData } from '../types';
import { getCharacterCount, decodeSmsMessage } from '../types';
import { isValidPhone } from '../../_shared/phone';
import { PRIORITIES } from '../../types';

export function SmsTestTab({
  templates,
  loading,
  onSend,
}: SmsTestTabProps) {
  const [sending, setSending] = useState(false);
  const [charInfo, setCharInfo] = useState({ count: 0, segments: 1 });

  const form = useForm<SmsTestData>({
    initialValues: {
      useTemplate: false,
      templateId: undefined,
      cellNumber: '',
      message: '',
      priority: 5,
      scheduledDateTime: undefined,
      addToQueue: true,
    },
    validate: {
      cellNumber: (value) => {
        if (!value || value.trim() === '') return 'Phone number is required';
        if (!isValidPhone(value.trim())) {
          return 'Enter a valid number, e.g. 0794559874 or +27794559874';
        }
        return null;
      },
      message: (value) => {
        if (!value || value.trim() === '') return 'Message is required';
        if (value.length > 1600) return 'Message too long (max 1600 chars for 10 segments)';
        return null;
      },
    },
  });

  // Update character count when message changes
  useEffect(() => {
    const info = getCharacterCount(form.values.message);
    setCharInfo(info);
  }, [form.values.message]);

  // Load template when selected
  useEffect(() => {
    if (form.values.useTemplate && form.values.templateId) {
      const template = templates.find((t) => t.templateID === form.values.templateId);
      if (template) {
        form.setFieldValue('message', decodeSmsMessage(template.message));
      }
    }
  }, [form.values.useTemplate, form.values.templateId, templates]);

  const handleSubmit = async (values: SmsTestData, addToQueue: boolean) => {
    setSending(true);
    try {
      await onSend({ ...values, addToQueue });
      form.reset();
      setCharInfo({ count: 0, segments: 1 });
    } catch (err) {
      // Error handled by hook
    } finally {
      setSending(false);
    }
  };

  const templateOptions = templates
    .filter((t) => t.isActive)
    .map((t) => ({
      value: String(t.templateID),
      label: t.templateName || 'Unnamed Template',
    }));

  const priorityOptions = PRIORITIES.map((p) => ({
    value: String(p.value),
    label: p.label,
  }));

  return (
    <form onSubmit={form.onSubmit((values) => handleSubmit(values, true))}>
      <Stack gap="md">
        <Alert
          color="blue"
          icon={<IconInfoCircle size={16} />}
          title="SMS Testing"
        >
          Draft and send test SMS messages or add them to the queue for scheduled delivery.
          You can enter a local number (e.g. 0794559874) — it's converted to international format automatically.
        </Alert>

        <Card withBorder padding="lg">
          <Stack gap="md">
        {/* Template Selection */}
        <Switch
          label="Use SMS Template"
          description="Load content from an existing template"
          {...form.getInputProps('useTemplate', { type: 'checkbox' })}
        />

        {form.values.useTemplate && (
          <Select
            label="Select Template"
            placeholder="Choose a template"
            data={templateOptions}
            value={form.values.templateId ? String(form.values.templateId) : null}
            onChange={(value) =>
              form.setFieldValue('templateId', value ? parseInt(value, 10) : undefined)
            }
            searchable
            clearable
          />
        )}

        {/* Phone Number */}
        <TextInput
          label="Phone Number"
          placeholder="0794559874 or +27794559874"
          required
          leftSection={<IconPhone size={16} />}
          description="Local (0…) or international (+27…) — converted automatically"
          {...form.getInputProps('cellNumber')}
        />

        {/* Message */}
        <div>
          <Textarea
            label="Message"
            placeholder="Enter your SMS message..."
            required
            rows={6}
            {...form.getInputProps('message')}
          />
          <Group justify="space-between" mt="xs">
            <Group gap="xs">
              <Badge size="sm" variant="light">
                {charInfo.count} characters
              </Badge>
              <Badge
                size="sm"
                variant="light"
                color={charInfo.segments > 3 ? 'red' : charInfo.segments > 1 ? 'orange' : 'green'}
              >
                {charInfo.segments} segment{charInfo.segments > 1 ? 's' : ''}
              </Badge>
            </Group>
            <Text size="xs" c="dimmed">
              {charInfo.segments <= 1 ? '160 chars per segment' : '153 chars per segment'}
            </Text>
          </Group>
          {charInfo.segments > 5 && (
            <Alert color="orange" icon={<IconAlertCircle size={14} />} mt="xs">
              Warning: Message will be sent in {charInfo.segments} parts. Consider shortening your message.
            </Alert>
          )}
        </div>

        {form.errors.message && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {form.errors.message}
          </Alert>
        )}

        {/* Priority */}
        <Select
          label="Priority"
          description="Higher priority SMS are sent first (1=lowest, 10=highest)"
          data={priorityOptions}
          value={String(form.values.priority)}
          onChange={(value) => form.setFieldValue('priority', parseInt(value || '5', 10))}
        />

        {/* Scheduled Send */}
        <TextInput
          label="Schedule Send (Optional)"
          placeholder="Leave empty to send immediately"
          type="datetime-local"
          leftSection={<IconClock size={16} />}
          value={form.values.scheduledDateTime ? new Date(form.values.scheduledDateTime).toISOString().slice(0, 16) : ''}
          onChange={(e) => {
            const value = e.target.value;
            form.setFieldValue('scheduledDateTime', value ? new Date(value) : undefined);
          }}
        />

        <Divider />

        {/* Action Buttons */}
        <Group justify="flex-end" gap="md">
          <Button
            variant="subtle"
            onClick={() => {
              form.reset();
              setCharInfo({ count: 0, segments: 1 });
            }}
            disabled={sending}
          >
            Clear Form
          </Button>

          <Button
            variant="outline"
            onClick={() => handleSubmit(form.values, true)}
            loading={sending}
            leftSection={<IconClock size={16} />}
            disabled={!form.isValid()}
          >
            Add to Queue
          </Button>

          <Button
            type="submit"
            gradientVariant="secondary"
            loading={sending}
            leftSection={<IconSend size={16} />}
          >
            Send Now
          </Button>
        </Group>
          </Stack>
        </Card>
      </Stack>
    </form>
  );
}
